import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { usePageSEO } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, ArrowRight, CheckCircle, XCircle } from "lucide-react";
import { motion } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BlueprintInvitePage() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  usePageSEO({ title: "Blueprint Invitation", noIndex: true });
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regName, setRegName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/blueprint-invite/${token}`);
        if (!res.ok) throw new Error("Invalid invitation");
        const data = await res.json();
        setInvite(data);
        setRegEmail(data.email || "");
        setRegName(data.name || "");
      } catch (e) {
        setError("This invitation is invalid or has expired.");
      }
      setLoading(false);
    };
    load();
  }, [token]);

  const handleStart = async () => {
    if (user) {
      navigate("/blueprint");
      return;
    }
    setSubmitting(true);
    try {
      // Register or login
      try {
        await api.post("/auth/register", { email: regEmail, password: regPassword, name: regName, company_name: invite?.company || "" });
      } catch (regErr) {
        // Already exists — try login
        await api.post("/auth/login", { email: regEmail, password: regPassword });
      }
      navigate("/blueprint");
    } catch (e) {
      setError("Could not create account. Please try logging in first.");
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0033CC]" /></div>;

  if (error && !invite) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-slate-200 p-10 max-w-md w-full text-center">
          <XCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mb-2">Invitation Not Found</h1>
          <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-6">{error}</p>
          <Link to="/" className="text-sm text-[#0033CC] font-['Outfit'] hover:underline">Go to stansbury.ai</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="blueprint-invite-page">
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50 px-6 md:px-12">
        <div className="max-w-4xl mx-auto flex items-center h-16">
          <Link to="/"><img src="/stansbury-logo.png" alt="Stansbury AI" className="h-10 sm:h-12" /></Link>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">You've Been Invited</p>
          <h1 className="text-3xl font-['Outfit'] font-medium text-[#0F172A] tracking-tight mb-3">
            Your AI Transformation Blueprint
          </h1>
          <p className="text-base text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed mb-8">
            {invite?.company ? `${invite.company} has been` : "You've been"} selected for a comprehensive operational assessment that maps revenue leaks, AI transformation opportunities, and implementation roadmap.
          </p>

          {/* What's included */}
          <div className="bg-white border border-slate-200 p-6 mb-6">
            <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-3">What You'll Receive</h3>
            <div className="grid grid-cols-2 gap-2">
              {["Revenue Leak Analysis", "AI Opportunity Matrix", "Department Transformation Plans", "Implementation Roadmap", "ROI Projections", "Executive PDF Report"].map(item => (
                <div key={item} className="flex items-center gap-2 text-xs text-slate-600 font-['IBM_Plex_Sans']">
                  <CheckCircle className="w-3 h-3 text-[#0033CC] flex-shrink-0" />{item}
                </div>
              ))}
            </div>
          </div>

          {/* Price */}
          {invite?.price > 0 && (
            <div className="bg-[#0F172A] p-6 mb-6 text-center">
              <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#818CF8] mb-2">Your Investment</p>
              <p className="text-3xl font-['Outfit'] font-light text-white">${invite.price.toLocaleString()}</p>
            </div>
          )}

          {/* Auth / Start */}
          {user ? (
            <Button onClick={() => navigate("/blueprint")} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-12 text-base font-['Outfit'] font-medium" data-testid="start-blueprint-btn">
              Begin Your Blueprint <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Create Your Account to Get Started</h3>
              {error && <p className="text-xs text-red-600 mb-3">{error}</p>}
              <div className="space-y-3 mb-4">
                <div>
                  <label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Full Name</label>
                  <Input value={regName} onChange={e => setRegName(e.target.value)} className="rounded-none border-slate-300 h-10" data-testid="invite-name" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Email</label>
                  <Input value={regEmail} onChange={e => setRegEmail(e.target.value)} type="email" className="rounded-none border-slate-300 h-10" data-testid="invite-email" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Create Password</label>
                  <Input value={regPassword} onChange={e => setRegPassword(e.target.value)} type="password" placeholder="Min. 6 characters" className="rounded-none border-slate-300 h-10" data-testid="invite-password" />
                </div>
              </div>
              <Button onClick={handleStart} disabled={submitting || !regEmail || !regPassword || regPassword.length < 6} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-12 text-base font-['Outfit'] font-medium" data-testid="start-blueprint-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Create Account & Begin <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
              <p className="text-xs text-slate-400 text-center mt-3 font-['IBM_Plex_Sans']">
                Already have an account? <Link to="/login" className="text-[#0033CC] hover:underline">Sign in</Link>
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
